import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Select, Skeleton, Statistic } from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  FileSearchOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { HowToCard } from '@/components/how-to-card';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';
import CreateRequestModal from './CreateRequestModal';
import RequestTabs from './RequestTabs';
import SendControls from './SendControls';
import { errorText } from './helpers';

const TABLE_DEFAULTS = Object.freeze({ offset: '1', limit: '50' });
const WRITE_ROLES = ['techAdmin', 'superAdmin', 'admin'];
const HOW_TO_ICONS = [PlusOutlined, CheckCircleOutlined, EditOutlined, MailOutlined, SendOutlined, FileSearchOutlined];

/**
 * Requests to a company's partners for their analytic cards: the list comes
 * from the books, HLB confirms the addresses, and the mail leaves in slow
 * batches that stop on a complaint or too many bounces.
 *
 * @param {Object} props
 * @param {string} props.companyId
 * @returns {JSX.Element}
 */
export default function PartnerRequestsPanel({ companyId }) {
  const { t } = useTranslation('partnerRequests');
  const queryClient = useQueryClient();
  const roles = useAuthStore((state) => state.user?.roles) || [];
  const canWrite = roles.some((role) => WRITE_ROLES.includes(role));
  const [requestId, setRequestId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const list = useQuery({
    queryKey: ['partnerRequests', 'list', companyId],
    queryFn: () => API.partnerRequests.getRequests(companyId),
  });

  useEffect(() => {
    setRequestId(null);
  }, [companyId]);

  useEffect(() => {
    if (!requestId && list.data?.length) setRequestId(list.data[0]._id);
  }, [list.data, requestId]);

  const one = useQuery({
    queryKey: ['partnerRequests', requestId, 'one'],
    queryFn: () => API.partnerRequests.getRequest(companyId, requestId),
    enabled: Boolean(requestId),
    refetchInterval: (query) => (query.state.data?.status === 'sending' ? 30000 : false),
  });
  const request = one.data;

  const changed = () => {
    queryClient.invalidateQueries({ queryKey: ['partnerRequests', 'list', companyId] });
    queryClient.invalidateQueries({ queryKey: ['partnerRequests', requestId, 'one'] });
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await API.partnerRequests.refreshRecipients(companyId, requestId);
      queryClient.invalidateQueries({ queryKey: ['partnerRequests', requestId] });
    } finally {
      setRefreshing(false);
    }
  };

  const steps = t('howTo.steps', { returnObjects: true });
  const counts = request?.counts;
  const status = counts?.status || {};
  const sentTotal = ['sent', 'delivered', 'bounced', 'complaint'].reduce((sum, k) => sum + (status[k] || 0), 0);

  return (
    <div className="grid gap-4">
      <HowToCard
        title={t('howTo.title')}
        open={list.data ? !list.data.length : undefined}
        note={t('howTo.note')}
        sections={[
          {
            title: t('howTo.section'),
            steps: (Array.isArray(steps) ? steps : []).map((step, i) => ({ ...step, icon: HOW_TO_ICONS[i] })),
          },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Select
          className="min-w-[280px] flex-1"
          loading={list.isLoading}
          placeholder={t('selectRequest')}
          value={requestId}
          onChange={setRequestId}
          options={(list.data || []).map((r) => ({ value: r._id, label: r.name }))}
        />
        {canWrite && (
          <Button shape="round" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            {t('create.open')}
          </Button>
        )}
        {canWrite && request && (
          <Button shape="round" icon={<ReloadOutlined />} loading={refreshing} onClick={refresh}>
            {t('refresh')}
          </Button>
        )}
      </div>

      {list.isError && <Alert type="error" showIcon message={errorText(t, list.error)} />}
      {list.data && !list.data.length && <Empty description={t('empty')} />}
      {requestId && one.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {one.isError && <Alert type="error" showIcon message={errorText(t, one.error)} />}

      {request && (
        <>
          <Card size="small">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex flex-wrap gap-6">
                <Statistic title={t('stats.total')} value={counts.total} prefix={<TeamOutlined />} />
                <Statistic title={t('stats.missing')} value={counts.missing} />
                <Statistic title={t('stats.proposed')} value={counts.proposed} />
                <Statistic title={t('stats.confirmed')} value={counts.confirmed} />
                <Statistic title={t('stats.sent')} value={sentTotal} />
                <Statistic title={t('stats.bounced')} value={status.bounced || 0} />
                <Statistic title={t('stats.complaint')} value={status.complaint || 0} />
                <Statistic title={t('stats.replied')} value={counts.replied} />
              </div>
              <SendControls companyId={companyId} request={request} canWrite={canWrite} onChanged={changed} />
            </div>
          </Card>
          <QueryParamsProvider defaultParams={TABLE_DEFAULTS}>
            <RequestTabs companyId={companyId} request={request} canWrite={canWrite} onChanged={changed} />
          </QueryParamsProvider>
        </>
      )}

      <CreateRequestModal
        open={creating}
        companyId={companyId}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          queryClient.invalidateQueries({ queryKey: ['partnerRequests', 'list', companyId] });
          setRequestId(created._id);
        }}
      />
    </div>
  );
}
